import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { WEBSITE_OPTIONS, type Website } from "../../../constants/features.js";

export type FilterChanges = Partial<
  Record<"reason" | "website" | "company" | "unresolved" | "page", string>
>;

function parsePage(value: string | null): number {
  if (value === null || !/^\d+$/.test(value)) {
    return 1;
  }

  return Math.max(1, Number(value));
}

function parseWebsite(value: string | null): Website | undefined {
  return WEBSITE_OPTIONS.find(option => option.value === value)?.value;
}

export function useFirmLogoCopyFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reason = searchParams.get("reason")?.trim() || undefined;
  const website = parseWebsite(searchParams.get("website"));
  const company = searchParams.get("company")?.trim() || undefined;
  const unresolved = searchParams.get("unresolved") === "true" ? true : undefined;
  const page = parsePage(searchParams.get("page"));

  const updateFilters = (changes: FilterChanges) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);

      for (const [key, value] of Object.entries(changes)) {
        if (value === undefined || value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }

      if (!("page" in changes)) {
        next.delete("page");
      }

      return next;
    });
  };

  const replacePage = useCallback(
    (nextPage: number) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);

          if (nextPage === 1) {
            next.delete("page");
          } else {
            next.set("page", String(nextPage));
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { reason, website, company, unresolved, page, updateFilters, replacePage };
}
